import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createFeedback,
  categoriesList,
  fetchpackageDeginationsList,
  updateFeedback,
  fetchFeedbackById,
  fetchGalleryImageUpdate,
} from "../../services/userservice";
import { Modal, Button, Alert } from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useDispatch, useSelector } from "react-redux";
import {
  setLoading as setCategoriesLoading,
  setError,
  setCategories,
} from "../../features/common/categorySlice";

const AddFeedback = () => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => setIsOpen(!isOpen);
  const dropdownRef = useRef(null);

  // ✅ formData
  const [formData, setFormData] = useState({
    feedback_type: "",
    category: "",
    title: "",
    sub_title: "",
    name: "",
    content: "",
    rating: "",
    category_type: "",
    holidayDestinationId: "",
    video_url: "",
    image_url: "",
    packageCategoryIds: [],
    status: "active",
  });
  const [imageURL, setImageURL] = useState(formData?.image_url);

  // Input Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Checkbox Change
  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    const selectedId = parseInt(value);

    setFormData((prev) => {
      const updatedCategories = checked
        ? [...prev.packageCategoryIds, selectedId] // Add if checked
        : prev.packageCategoryIds.filter((id) => id !== selectedId); // Remove if unchecked

      return { ...prev, packageCategoryIds: updatedCategories };
    });
  };

 

  useEffect(() => {
    if (id) {
      const fetchFeedback = async () => {
        try {
          setLoading(true);
          const res = await fetchFeedbackById(id); 
        
          setFormData({
            feedback_type: res.feedback_type || null,
            category: res.category || null,
            title: res.title || "",
            sub_title: res.sub_title || "",
            name: res.name || "",
            content: res.content || "",
            rating: res.rating || null,
            category_type: res.category_type || 1,
            holidayDestinationId: res.holidayDestinationId || null,
            packageCategoryIds: (res.packageCategories || []).map(
              (pc) => pc.packageCategory?.id
            ),
            video_url: res?.video_url,
            image_url: res?.image_url,
            status: res.status || "active",
          });
        } catch (err) {
          setErrorMsg(err.message || "Error loading feedback");
        } finally {
          setLoading(false);
        }
      };
      fetchFeedback();
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);

    try {
      const payload = {
        ...formData,
        image_url: formData.image_url?.includes("http")
          ? formData.image_url.split("/").pop()
          : formData.image_url,
        feedback_type: parseInt(formData.feedback_type) || null,
        category: parseInt(formData.category) || null,
        category_type: parseInt(formData.category_type) || 1,
        holidayDestinationId: formData.holidayDestinationId
          ? parseInt(formData.holidayDestinationId)
          : null,
        rating: formData.rating ? parseFloat(formData.rating) : null,
        video_url: formData.video_url || null,
      };

      if (payload.rating < 0 || payload.rating > 5) {
        setErrorMsg("Rating must be between 0 and 5");
        setLoading(false);
        return;
      }

      console.log("Submitting payload:", payload);

      if (id) {
        // Update feedback
        await updateFeedback(id, payload);
      } else {
        // Create new feedback
        await createFeedback(payload);
      }

      setShowModal(true);
      setTimeout(() => {
        navigate("/feedbacks");
        setShowModal(false);
      }, 2000);
    } catch (err) {
      setErrorMsg(err.message || "Error while submitting feedback");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Load Categories (Redux)
  const { categories } = useSelector((state) => state?.category);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(setCategoriesLoading());
        const response = await categoriesList();
        dispatch(setCategories(response));
      } catch (error) {
        dispatch(setError(error.message));
      }
    };
    fetchData();
  }, [dispatch]);

  // ✅ Load Destinations
  const [packageDesignations, setPackageDesignations] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchpackageDeginationsList();
        setPackageDesignations(response?.data || []);
      } catch (error) {
        console.log("Error fetching destinations:", error);
      }
    };
    fetchData();
  }, []);

  // ✅ Selected category names
  const selectedCategoryNames =
    formData.packageCategoryIds
      .map((id) => categories.find((category) => category.id === id)?.name)
      .filter(Boolean)
      .join(", ") || "Select Categories";

  const handleGalleryChange = async (e) => {
    const files = Array.from(e.target.files);
    const formDataData = new FormData();

    files.forEach((file) => {
      formDataData.append("profile_pic", file);
      formDataData.append("type", "gallery");
    });

    try {
      const response = await fetchGalleryImageUpdate(formDataData);
      const uploadedFile = response?.data?.files?.[0];

      if (uploadedFile) {
        const { image_url, base_url } = uploadedFile;

        setFormData((prev) => ({
          ...prev,
          image_url: image_url,
        }));

        setImageURL(base_url + image_url);

        console.log("Image upload successful:", image_url);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">Add Feedback</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Home</Link>
                </li>
                <li className="breadcrumb-item active">Add Feedback</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          {errorMsg && (
            <Alert variant="danger" className="text-center">
              {errorMsg}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <div className="card">
              <div className="card-body">
                <div className="row">
                  {/* Feedback Type */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>Feedback Type</label>
                      <select
                        className="form-control"
                        name="feedback_type"
                        value={formData.feedback_type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value={1}>📝 Testimonial</option>
                        <option value={2}>⭐ Review</option>
                      </select>
                    </div>
                  </div>

                  {/* Category */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>Category</label>
                      <select
                        className="form-control"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value={1}>Holiday</option>
                        <option value={2}>Hotel</option>
                        <option value={3}>Flight</option>
                      </select>
                    </div>
                  </div>

                  {/* Title */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Enter Title"
                      />
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>Sub Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="sub_title"
                        value={formData.sub_title}
                        onChange={handleInputChange}
                        placeholder="Enter Sub Title"
                      />
                    </div>
                  </div>

                  {/* Name */}
                  <div className="col-sm-4">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter Name"
                        required
                      />
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="col-sm-4">
                    <div className="form-group">
                      <label>Rating</label>
                      <input
                        type="number"
                        step="0.1"
                        min="1"
                        max="5"
                        className="form-control"
                        name="rating"
                        value={formData.rating}
                        onChange={handleInputChange}
                        placeholder="4.5"
                      />
                    </div>
                  </div>

                  {/* Holiday Fields only when category = holiday */}
                  {formData.category == "1" && (
                    <>
                      {/* Destination */}
                      <div className="col-sm-4">
                        <div className="form-group">
                          <label>Holiday Destination</label>
                          <select
                            className="form-control"
                            name="holidayDestinationId"
                            value={formData.holidayDestinationId}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select</option>
                            {packageDesignations.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Package Categories */}
                      <div className="col-sm-12">
                        <div className="form-group">
                          <label>Package Category</label>
                          <div
                            className="custom-dropdown-container"
                            ref={dropdownRef}
                          >
                            <div
                              className="custom-dropdown"
                              onClick={toggleDropdown}
                            >
                              <input
                                type="text"
                                className="dropdown-input"
                                value={selectedCategoryNames}
                                readOnly
                                placeholder="Select Categories"
                              />
                              <span className="arrow">
                                {isOpen ? "▲" : "▼"}
                              </span>
                            </div>

                            {isOpen && (
                              <div className="dropdown-options">
                                {categories.map((option) => (
                                  <div
                                    key={option.id}
                                    className="checkbox-container"
                                  >
                                    <input
                                      type="checkbox"
                                      id={`category-${option.id}`}
                                      value={option.id}
                                      checked={formData.packageCategoryIds.includes(
                                        option.id
                                      )}
                                      onChange={handleCheckboxChange}
                                    />
                                    <label htmlFor={`category-${option.id}`}>
                                      {option.name}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Content */}
                  <div className="col-sm-12">
                    <div className="form-group">
                      <label>Description</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={formData.content}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          setFormData((prev) => ({
                            ...prev,
                            content: data,
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-sm-4">
                    <div className="form-group">
                      <label>Type</label>
                      <select
                        className="form-control"
                        name="category_type"
                        value={formData.category_type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value={1}>Text</option>
                        <option value={2}>Video</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-sm-4">
                    <div className="form-group">
                      <label>Video</label>
                      <input
                        type="text"
                        className="form-control"
                        name="video_url"
                        value={formData.video_url}
                        onChange={handleInputChange}
                        placeholder="Enter Youtube Url"
                        
                      />
                    </div>
                  </div>

                  <div className="col-sm-4">
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        className="form-control"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-sm-12">
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label>Image (Upload Images)</label>
                        <input
                          type="file"
                          multiple
                          className="form-control"
                          onChange={handleGalleryChange}
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      {imageURL || formData.image_url ? (
                        <>
                          <label>Uploaded Image</label>
                          <br />
                          <img
                            src={
                              imageURL
                                ? imageURL
                                : formData.image_url?.includes("http")
                                ? formData.image_url
                                : `https://d1dejdv71ksru4.cloudfront.net/media/holiday/690x410/${formData.image_url}`
                            }
                            alt="Feedback"
                            style={{ width: "100%", borderRadius: "10px" }}
                          />
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-footer">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Feedback"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Success Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <i
              className="fa fa-check-circle text-success mb-4"
              style={{ fontSize: "4rem" }}
            ></i>
            <h4>Feedback submitted successfully!</h4>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AddFeedback;
